/**
 * space/model.js — one space, and the data shapes that fill it.
 *
 * A "model" is the portable description of a space: themes, prizes, fish,
 * arrangement, decorations and guestbook. Your space builds one from `save`,
 * Gustav's is generated deterministically, and a visitor's arrives over the
 * wire from `serialize()`. Because all three are the same shape, the rendering
 * code has no idea which is which — which is exactly what makes the read-only
 * visitor view free rather than a second implementation.
 *
 * @module space/model
 */

import { SPACE, STALLS, ROOM_THEMES, SHELF_THEMES, SPACE_MUSIC } from '../constants.js';
import { save } from '../core/save.js';
import { mulberry32 } from '../core/testmode.js';
import { SpaceRoom } from './space-world.js';
import { ShelfWall } from './shelves.js';
import { FishTank } from './fish-tank.js';
import { DecorSet } from './decor.js';
import { Guestbook } from './guestbook.js';

/**
 * @typedef {Object} SpaceModel
 * @property {string} owner
 * @property {string} theme      Shelf theme id.
 * @property {string} roomTheme  Floor/wall theme id.
 * @property {string} music      Track id.
 * @property {number} shelfSlots
 * @property {number} tankCapacity
 * @property {Array<object>} plush
 * @property {Array<object>} fish
 * @property {Record<string, number>} arrangement prizeId → slot
 * @property {Array<{anchor: string, catalogId: string}>} decor
 * @property {Array<object>} guestbook
 */

/** @returns {SpaceModel} An empty space, used as the base for merges. */
export function blankModel() {
  return {
    owner: 'A goose',
    theme: SHELF_THEMES[0].id,
    roomTheme: ROOM_THEMES[0].id,
    music: SPACE_MUSIC[0].id,
    shelfSlots: SPACE.shelfSlotsBase,
    tankCapacity: 5,
    plush: [],
    fish: [],
    arrangement: {},
    decor: [],
    guestbook: [],
  };
}

/** @returns {SpaceModel} The player's space, read straight out of the save. */
export function playerModel() {
  const s = save.data.space;
  return {
    owner: 'You',
    theme: s.theme,
    roomTheme: s.roomTheme,
    music: s.music,
    shelfSlots: s.shelfSlots,
    tankCapacity: s.tankCapacity,
    plush: save.data.prizes.plush.slice(),
    fish: save.data.prizes.fish.slice(),
    arrangement: { ...s.arrangement },
    decor: s.decor.slice(),
    guestbook: s.guestbook.slice(),
  };
}

/** Names Gustav gave his fish. Fixed, because Gustav is the same for everyone. */
const GUSTAV_FISH = 'Bubbles|Sir Fins-a-Lot|Honk Solo|Gill-bert|Marina|Nibbles'.split('|');

/**
 * A neighbour worth visiting. Generated from a fixed seed rather than stored,
 * so a solo player sees a furnished space the first time they open the game and
 * the same one every time after.
 * @returns {SpaceModel}
 */
export function npcModel() {
  const r = mulberry32(0x6005e1);
  const model = blankModel();
  model.owner = 'Gustav';
  model.theme = 'golden-trophy';
  model.roomTheme = 'neon-glow';
  model.shelfSlots = 18;
  model.tankCapacity = 20;

  for (let i = 0; i < 11; i++) {
    const stall = STALLS[Math.floor(r() * STALLS.length)];
    model.plush.push({
      id: `gustav-plush-${i}`,
      kind: r() > 0.62 ? 'plush-large' : 'plush-small',
      stallId: stall.id,
      variant: null,
      day: 19_000 + i,
    });
    model.arrangement[`gustav-plush-${i}`] = i;
  }

  for (let i = 0; i < 8; i++) {
    const roll = r();
    model.fish.push({
      id: `gustav-fish-${i}`,
      variant: roll > 0.86 ? 'golden' : roll > 0.55 ? 'spotted' : 'orange',
      name: GUSTAV_FISH[i % GUSTAV_FISH.length] + (i >= GUSTAV_FISH.length ? ' II' : ''),
      stallId: r() > 0.5 ? 'ping-pong-goldfish' : 'hook-a-duck',
      day: 19_000 + i * 3,
    });
  }

  model.decor.push({ anchor: 'tank-0', catalogId: 'castle' });
  model.decor.push({ anchor: 'tank-1', catalogId: 'plants' });
  model.decor.push({ anchor: 'tank-2', catalogId: 'treasure-chest' });
  model.decor.push({ anchor: 'room-1', catalogId: 'neon-sign' });
  model.guestbook.push({ reaction: 'wow', emoji: '🤩', from: 'Beryl', day: 19_012 });
  model.guestbook.push({ reaction: 'honk', emoji: '📣', from: 'Nigel', day: 19_014 });
  return model;
}

/**
 * One room plus the four systems that furnish it.
 *
 * `readOnly` instances skip every plaque, plinth and pointer registration, so a
 * visitor can look but not spend your Stars.
 */
export class SpaceInstance {
  /**
   * @param {{ name: string, origin: object, title: string, readOnly?: boolean,
   *           hooks?: object }} opts
   */
  constructor(opts) {
    this.name = opts.name;
    this.readOnly = !!opts.readOnly;
    this.hooks = opts.hooks || {};
    /** @type {SpaceModel|null} */ this.model = null;
    this.room = new SpaceRoom({
      name: opts.name, origin: opts.origin, title: opts.title,
    });
  }

  /** @param {import('three').Scene} scene */
  build(scene) {
    const h = this.hooks;
    this.room.build(scene);

    this.shelves = new ShelfWall({
      room: this.room, readOnly: this.readOnly,
      onBuyExpansion: h.onBuyExpansion, onOpenThemes: h.onOpenThemes,
    });
    this.tank = new FishTank({
      room: this.room, readOnly: this.readOnly,
      onUpgrade: h.onUpgradeTank, onRename: h.onRenameFish,
    });
    this.decor = new DecorSet({
      room: this.room, tank: this.tank, readOnly: this.readOnly,
      onPlace: h.onPlaceDecor, onRemove: h.onRemoveDecor,
      onRoomTheme: h.onRoomTheme, onMusic: h.onMusic, stars: h.stars,
    });
    this.guestbook = new Guestbook({
      room: this.room, readOnly: this.readOnly, onSign: h.onSign,
    });
    return this;
  }

  /**
   * Re-furnish from a model. Cheap enough to run on every purchase, which
   * removes a whole class of partial-update bugs.
   * @param {SpaceModel} model
   */
  apply(model) {
    this.model = model;
    this.room.applyTheme(model.roomTheme);
    this.shelves.rebuild(model);
    // The tank owns the anchors its decorations stand on, so it rebuilds first.
    this.tank.rebuild(model);
    this.decor.rebuild(model);
    this.guestbook.rebuild(model);
  }

  /**
   * @param {number} dt
   * @param {import('three').Camera} camera
   */
  update(dt, camera) {
    this.shelves.update(dt);
    this.tank.update(dt, camera);
    this.decor.update(dt);
    this.guestbook.update(dt);
  }
}
